import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, ChevronUp, ChevronDown, Trash2, CirclePlus } from 'lucide-react';
import { CourseData } from '../../types/CourseData';
import { triggerPiepeline } from '../../services/api/virtualAssistant';

interface CoursesSidebarProps {
  courses: CourseData[];
  setCourses: React.Dispatch<React.SetStateAction<CourseData[]>>;
  selectedCourse: number;
  setSelectedCourse: (courseId: number) => void;
  addNewCourse: () => void;
  systemConfig: { Id: string; ConfigValue: string; }[];
  showDeleteConfirmationModal: (message: string, onConfirm: () => void) => void;
  connectedCourseIds: string[];
  setConnectedCourseIds: React.Dispatch<React.SetStateAction<string[]>>;
  currentStatus: { progress: number; status: string; error?: string; } | null;
  triggerAutoSave: () => void;
  fetchCalledRef: React.MutableRefObject<{ [key: number]: boolean }>;
  isCanvasFullLoad: boolean;
  triggerPiepeline: typeof triggerPiepeline;
  assistant: any;
  setPendingSave: React.Dispatch<React.SetStateAction<boolean>>;
  fetchPipelineData: () => void;
  setCustomModal: React.Dispatch<React.SetStateAction<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "loading" | "confirmation" | "warning";
    onConfirm?: () => void;
  }>>;
}

interface ContextMenu {
  visible: boolean;
  x: number;
  y: number;
  courseId: number;
}

const CoursesSidebar: React.FC<CoursesSidebarProps> = ({
  courses,
  setCourses,
  selectedCourse,
  setSelectedCourse,
  addNewCourse,
  systemConfig,
  showDeleteConfirmationModal,
  connectedCourseIds,
  setConnectedCourseIds,
  currentStatus,
  triggerAutoSave,
  fetchCalledRef,
  isCanvasFullLoad,
  triggerPiepeline,
  assistant,
  setPendingSave,
  fetchPipelineData,
  setCustomModal
}) => {
  const [contextMenu, setContextMenu] = useState<ContextMenu>({
    visible: false,
    x: 0,
    y: 0,
    courseId: 0
  });

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const handleContextMenu = (e: React.MouseEvent, courseId: number) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      courseId
    });
  };

  const closeContextMenu = () => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  };

  const moveCourse = (courseId: number, direction: 'up' | 'down') => {
    setCourses((prevCourses) => {
      const sortedCourses = [...prevCourses].sort((a, b) => a.order - b.order);
      const currentIndex = sortedCourses.findIndex((course) => course.id === courseId);
      
      if (
        (direction === 'up' && currentIndex === 0) ||
        (direction === 'down' && currentIndex === sortedCourses.length - 1)
      ) {
        return prevCourses;
      }
      
      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

      const updatedCourses = sortedCourses.map((course, index) => {
        if (index === currentIndex) {
          return { ...course, order: sortedCourses[newIndex].order };
        }
        if (index === newIndex) {
          return { ...course, order: sortedCourses[currentIndex].order };
        }
        return course;
      });

      return updatedCourses;
    });
    closeContextMenu();
  };

  const deleteCourse = (courseId: number) => {
    const warningMessage = 'Are you sure you want to delete canvas course? This action cannot be undone.';
    showDeleteConfirmationModal(warningMessage, async () => {
      const courseid_prefix = systemConfig.find(item => item.Id === 'canvas_courseid_prefix')?.ConfigValue;

      setCustomModal((prev) => ({
        ...prev,
        isOpen: false,
      }));
              
      setCustomModal({
        isOpen: true,
        title: "Deleting...",
        message: warningMessage,
        type: "loading",
      });
    
      try {
        const courseToDelete = courses.find(c => c.id === courseId);
        const isConnected = courseToDelete?.connectionState === 'connected';
        const pipelineStatus = currentStatus?.status;

        setCourses(prev => {
          const filtered = prev.filter(c => c.id !== courseId);
          return filtered.map((course, index) => ({
            ...course,
            order: index + 1
          }));
        });
        
        if (selectedCourse === courseId) {
          const remainingCourses = courses.filter(c => c.id !== courseId);
          setSelectedCourse(remainingCourses[0]?.id || 0);
        }

        let courseUrlSegment = courseToDelete?.course_id;
        if (courseToDelete?.course_id) {
          if (courseToDelete?.course_id?.length < 7) {
            courseUrlSegment = courseid_prefix + courseToDelete?.course_id;
          }
          setConnectedCourseIds(prev =>
            prev.filter(id => id !== courseUrlSegment)
          );
        }

        if (isConnected) {
          triggerAutoSave();
           
          setTimeout(() => {
            const run_type = 'Adhoc';
            const full_load_value = String(isCanvasFullLoad) === 'true' ? 'false' : 'true';
            let queue_name = systemConfig.find(item => item.Id === 'canvas_queue_name')?.ConfigValue ?? '';
            const res = triggerPiepeline(assistant.app_id, run_type, queue_name, full_load_value);
            setPendingSave(false);
            setTimeout(() => {
              fetchPipelineData();
              setTimeout(() => {
                fetchPipelineData();
              }, 500);
            }, 500); 
            setCustomModal({
              isOpen: true,
              title: "Success!",
              message: 'Canvas Course deleted successfully',
              type: "success",
            });
          }, 20 * 1000);
        } else {
          setCustomModal({
            isOpen: true,
            title: "Success!",
            message: 'Canvas Course deleted successfully',
            type: "success",
          });
        }
      } catch (error) {
        console.log("delete failed", error);
      }
    });
    delete fetchCalledRef.current[courseId];
    closeContextMenu();
  };

  const handleDragStart = (e: React.DragEvent, position: number) => {
    e.stopPropagation();
    dragItem.current = position;
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnter = (e: React.DragEvent, position: number) => {
    e.preventDefault();
    e.stopPropagation();
    dragOverItem.current = position;
    e.currentTarget.classList.add('bg-gray-50');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('bg-gray-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    e.currentTarget.classList.remove('opacity-50');

    if (dragItem.current !== null && dragOverItem.current !== null) {
      const copyListItems = [...courses];
      const dragItemContent = copyListItems[dragItem.current];
      
      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(dragOverItem.current, 0, dragItemContent);
      
      const reorderedItems = copyListItems.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      setCourses(reorderedItems);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>, courseId: number) => {
    const updatedCourseName = event.target.value;
    setCourses(prevCourses => 
      prevCourses.map(course =>
        course.id === courseId 
          ? { ...course, course_name: updatedCourseName } 
          : course
      )
    );
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target as Node)) {
        setContextMenu(prev => ({ ...prev, visible: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [contextMenu]);

  const sortedCourses = [...courses].sort((a, b) => a.order - b.order);
  const maxAllowedCourses = Number(systemConfig.find(item => item.Id === 'va_max_allowed_canvas_course')?.ConfigValue) || 5;

  return (
    <div className="w-64 border-r border-gray-200 overflow-y-auto">
      {sortedCourses.map((course, index) => (
        <div
          key={course.id}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragEnter={(e) => handleDragEnter(e, index)}
          onDragLeave={handleDragLeave}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          className={`flex items-center justify-between p-4 cursor-move hover:bg-gray-50 
            ${selectedCourse === course.id ? 'bg-gray-100' : ''}
            transition-opacity duration-200`}
        >
          <div 
            className="flex items-center space-x-3"
            onClick={() => setSelectedCourse(course.id)}
          >
            <img src="/canvas-logo.svg" alt="Canvas Logo" className="h-5 w-5" />
            <span className="text-sm" title={course?.course_name}>
              <input 
                type="text" 
                value={course.course_name || `New Canvas Course ${course.order}`}
                onChange={(e) => handleNameChange(e, course.id)}
                className="text-sm bg-transparent border-none outline-none w-full truncate cursor-pointer"
                readOnly
              />
            </span>
          </div>
          <button
            className="hover:bg-gray-100 p-1 rounded"
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e, course.id);
            }}
          >
            <MoreVertical className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      ))}

      {courses.length < maxAllowedCourses && (
        <div className="p-4">
          <button
            onClick={addNewCourse}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
          >
            <CirclePlus className="h-6 w-6 text-gray-500" />
          </button>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white shadow-lg rounded-lg py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => moveCourse(contextMenu.courseId, 'up')}
          >
            <ChevronUp className="h-4 w-4" />
            <span>Move Up</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
            onClick={() => moveCourse(contextMenu.courseId, 'down')}
          >
            <ChevronDown className="h-4 w-4" />
            <span>Move Down</span>
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2 text-red-600"
            onClick={() => deleteCourse(contextMenu.courseId)}
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CoursesSidebar;